import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Service,
  CreateServiceInput,
  UpdateServiceInput,
} from '@/types/Service';
import { useToast } from '@/hooks/useToast';

interface ServiceFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (service: CreateServiceInput | UpdateServiceInput) => Promise<void>;
  service?: Service | null;
  agentId: number;
}

/**
 * How a service's price is stated, which the stored `price` column encodes:
 * `0` is free, `null` is "we do not publish one", and anything above 0 is the
 * amount. A blank box used to cover both of the first two, which read as free
 * in the dashboard but reached the agent as no price information at all.
 */
type PriceMode = 'fixed' | 'free' | 'unlisted';

const resolvePriceMode = (price: number | null | undefined): PriceMode => {
  if (price === null || price === undefined) return 'unlisted';
  return price > 0 ? 'fixed' : 'free';
};

export const ServiceForm: React.FC<ServiceFormProps> = ({
  isOpen,
  onClose,
  onSave,
  service,
  agentId,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const [price, setPrice] = useState<string>('');
  // New services start on 'fixed' with an empty amount: that is the common
  // case, and it cannot quietly claim a service is free the way a blank box
  // did -- an empty amount fails validation instead.
  const [priceMode, setPriceMode] = useState<PriceMode>('fixed');
  const [enabled, setEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Generate duration options in 5-minute increments (5 min to 4 hours)
  const durationOptions = useMemo(() => {
    return Array.from({ length: Math.floor(240 / 5) }, (_, i) => {
      const minutes = (i + 1) * 5; // Start from 5 minutes
      const hoursLabel = t('agent.services.duration.hours.translation', 'hr');
      const minutesLabel = t(
        'agent.services.duration.minutes.translation',
        'min'
      );

      return {
        value: minutes,
        label:
          minutes < 60
            ? `${minutes} ${minutesLabel}`
            : minutes % 60 === 0
              ? `${Math.floor(minutes / 60)} ${hoursLabel}`
              : `${Math.floor(minutes / 60)} ${hoursLabel} ${minutes % 60} ${minutesLabel}`,
      };
    });
  }, [t]);

  useEffect(() => {
    if (service) {
      setName(service.name);
      setDescription(service.description || '');
      // Round to nearest 5-minute increment if needed, ensuring it's at least 5 minutes
      const roundedDuration = Math.max(
        5,
        Math.round(service.duration_minutes / 5) * 5
      );
      // Ensure it doesn't exceed our maximum (240 minutes / 4 hours)
      const clampedDuration = Math.min(240, roundedDuration);
      setDurationMinutes(clampedDuration);
      const mode = resolvePriceMode(service.price);
      setPriceMode(mode);
      setPrice(mode === 'fixed' ? String(service.price) : '');
      setEnabled(service.enabled);
    } else {
      // Reset for new service
      setName('');
      setDescription('');
      setDurationMinutes(60);
      setPrice('');
      setPriceMode('fixed');
      setEnabled(true);
    }
  }, [service, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.services.errors.name_required.translation',
          'Name Required'
        ),
        description: t(
          'agent.services.errors.name_required_description.translation',
          'Please provide a service name.'
        ),
      });
      return;
    }

    if (durationMinutes <= 0 || durationMinutes % 5 !== 0) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.services.errors.invalid_duration.translation',
          'Invalid Duration'
        ),
        description: t(
          'agent.services.errors.duration_step.translation',
          'Duration must be a multiple of 5 minutes.'
        ),
      });
      return;
    }

    // free -> 0, unlisted -> null, fixed -> the amount. Only 'fixed' reads the
    // input, so switching away from it cannot leave a stale number behind.
    let priceValue: number | null = null;
    if (priceMode === 'free') {
      priceValue = 0;
    } else if (priceMode === 'fixed') {
      const parsed = parseFloat(price);
      if (price.trim() === '' || isNaN(parsed) || parsed <= 0) {
        toast({
          variant: 'destructive',
          title: t(
            'agent.services.errors.invalid_price.translation',
            'Invalid Price'
          ),
          description: t(
            'agent.services.errors.price_required.translation',
            'Enter an amount above zero, or choose Free or Price on request.'
          ),
        });
        return;
      }
      priceValue = parsed;
    }

    try {
      setIsSaving(true);
      const serviceData: CreateServiceInput | UpdateServiceInput = {
        agent_id: agentId,
        name: name.trim(),
        description: description.trim() || null,
        duration_minutes: durationMinutes,
        price: priceValue,
        enabled,
      };

      await onSave(serviceData);

      toast({
        title: service
          ? t('agent.services.updated.translation', 'Service Updated')
          : t('agent.services.created.translation', 'Service Created'),
      });

      onClose();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.services.errors.save_failed.translation',
          'Save Failed'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={open => !open && onClose()}>
      <ModalContent className="max-w-2xl">
        <ModalHeader>
          <ModalTitle
            description={t(
              'agent.services.form.description.translation',
              'Manage service details and pricing.'
            )}
          >
            {service
              ? t('agent.services.edit.translation', 'Edit Service')
              : t('agent.services.create.translation', 'Create Service')}
          </ModalTitle>
        </ModalHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>
              {t('agent.services.name.translation', 'Service Name')} *
            </Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder={t(
                'agent.services.name.placeholder.translation',
                'e.g., Dental Cleaning'
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>
              {t('agent.services.description.translation', 'Description')}
            </Label>
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder={t(
                'agent.services.description.placeholder.translation',
                'Describe what this service includes...'
              )}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                {t('agent.services.duration.label.translation', 'Duration')} *
              </Label>
              <Select
                value={durationMinutes.toString()}
                onValueChange={value => setDurationMinutes(parseInt(value, 10))}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      'agent.services.duration.placeholder.translation',
                      'Select duration'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {durationOptions.map(option => (
                    <SelectItem
                      key={option.value}
                      value={option.value.toString()}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="service-price-mode">
                {t('agent.services.price.label.translation', 'Price (EUR)')}
                {priceMode === 'fixed' ? ' *' : ''}
              </Label>
              <Select
                value={priceMode}
                onValueChange={value => setPriceMode(value as PriceMode)}
              >
                <SelectTrigger id="service-price-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">
                    {t(
                      'agent.services.price.mode.fixed.translation',
                      'Fixed price'
                    )}
                  </SelectItem>
                  <SelectItem value="free">
                    {t('agent.services.price.mode.free.translation', 'Free')}
                  </SelectItem>
                  <SelectItem value="unlisted">
                    {t(
                      'agent.services.price.mode.unlisted.translation',
                      "Don't say the price"
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
              {priceMode === 'fixed' && (
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  aria-label={t(
                    'agent.services.price.amount.translation',
                    'Price amount in EUR'
                  )}
                  placeholder={t(
                    'agent.services.price.placeholder.translation',
                    'e.g. 25.00'
                  )}
                />
              )}
              <p className="text-xs text-muted-foreground">
                {priceMode === 'free'
                  ? t(
                      'agent.services.price.hint.free.translation',
                      'The agent tells callers this service is free of charge.'
                    )
                  : priceMode === 'unlisted'
                    ? t(
                        'agent.services.price.hint.unlisted.translation',
                        'The agent will not quote a price and offers to connect the caller instead.'
                      )
                    : t(
                        'agent.services.price.hint.fixed.translation',
                        'The agent quotes this amount to callers.'
                      )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="enabled-switch">
              {t('agent.services.enabled.translation', 'Enabled')}
            </Label>
            <Switch
              id="enabled-switch"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>
        </div>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSaving}
          >
            {t('common.cancel.translation', 'Cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            variant="brand"
            disabled={isSaving}
          >
            {isSaving
              ? t('common.saving.translation', 'Saving...')
              : t('common.save.translation', 'Save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
